import React, { useState } from 'react';
import DetailsTable from 'components/pages/address/public/components/details-table';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import { TryScrambleText } from 'utils/text-scrambler';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { Room } from 'data/listing';
import { getRoomLevelLabelKey, getRoomTypeLabelKey } from 'types/reso';
import { listingPageModalIds } from 'constants/test-constants';

//#region Types
const UNIT_OF_LENGTH_FEET = 'feet' as const;
const UNIT_OF_LENGTH_METRES = 'metres' as const;
type UnitOfLength = typeof UNIT_OF_LENGTH_FEET | typeof UNIT_OF_LENGTH_METRES;

const VARIANT_GRID = 'grid' as const;
const VARIANT_TABLE = 'table' as const;
type RoomsVariant = typeof VARIANT_GRID | typeof VARIANT_TABLE;

type RoomsProps = {
  rooms: Room[];
  isPrivate?: boolean;
  shouldScramble?: boolean;
  onShowDetailsClick?: () => void;
  variant?: RoomsVariant;
};
//#endregion

//#region Components

function RoomDetails({ room, unitOfLength, shouldScramble }: { room: Room; unitOfLength: UnitOfLength; shouldScramble?: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  const size = room.size(unitOfLength);
  const sizeLabel = size === Room.SIZE_NOT_AVAILABLE_LABEL ? t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_SIZE_NOT_AVAILABLE) : size;
  return (
    <div className={styles.room}>
      <span>{t(getRoomTypeLabelKey(room.type), { defaultValue: `${room.type}` })}:</span>
      <span>{t(getRoomLevelLabelKey(room.level), { defaultValue: `${room.level}` })}</span>
      <span>
        {TryScrambleText(sizeLabel, shouldScramble)}
      </span>
    </div>
  );
}

function UnitsHeader({
  isPrivate,
  otherUnitOfLength,
  onToggleUnit,
  onShowDetailsClick,
}: {
  isPrivate?: boolean;
  otherUnitOfLength: UnitOfLength;
  onToggleUnit: () => void;
  onShowDetailsClick?: () => void;
}) {
  const { t } = useTranslation(Namespaces.COMMON);
  return isPrivate ? (
    <a className="primary-link" onClick={onShowDetailsClick}>
      {t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_SHOW_DETAILS, { defaultValue: 'Show Details' })}
    </a>
  ) : (
    <p className={buildClassName('primary-link', styles['units-toggle'])} onClick={onToggleUnit}>
      {t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_CHANGE_TO, { context: otherUnitOfLength, defaultValue: 'Change to {{unit}}' })}
    </p>
  );
}

function RoomsTable({ rooms, isPrivate, shouldScramble, unitOfLength, otherUnitOfLength, onToggleUnit, onShowDetailsClick }: {
  rooms: Room[];
  isPrivate?: boolean;
  shouldScramble?: boolean;
  unitOfLength: UnitOfLength;
  otherUnitOfLength: UnitOfLength;
  onToggleUnit: () => void;
  onShowDetailsClick?: () => void;
}) {
  const { t } = useTranslation(Namespaces.COMMON);
  const hasAnyFlooring = rooms.some(room => !!room.flooring);

  const roomLabel = t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_COL_ROOM, { defaultValue: 'Room' });
  const dimensionsLabel = t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_COL_DIMENSIONS, { defaultValue: 'Dimensions' });
  const levelLabel = t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_COL_LEVEL, { defaultValue: 'Level' });
  const flooringLabel = t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_COL_FLOORING, { defaultValue: 'Flooring' });

  return (
    <div
      className={buildClassName(styles.component, styles['table-variant'], isPrivate && styles['is-private'])}
      data-testid={listingPageModalIds.detailsTable}
    >
      <header className={styles['table-header']}>
        <UnitsHeader
          isPrivate={isPrivate}
          otherUnitOfLength={otherUnitOfLength}
          onToggleUnit={onToggleUnit}
          onShowDetailsClick={onShowDetailsClick}
        />
      </header>
      <div className={buildClassName(styles.table, !hasAnyFlooring && styles['no-flooring'])} role="table">
        <div className={styles['table-head']} role="row">
          <span role="columnheader">{roomLabel}</span>
          <span role="columnheader">{dimensionsLabel}</span>
          <span role="columnheader">{levelLabel}</span>
          {hasAnyFlooring && <span role="columnheader">{flooringLabel}</span>}
        </div>
        {rooms.map((room, index) => {
          const size = room.size(unitOfLength);
          const sizeLabel = size === Room.SIZE_NOT_AVAILABLE_LABEL
            ? t(LOCALE_COMMON_PATHS.ADDRESS_ROOMS_SIZE_NOT_AVAILABLE)
            : size;
          return (
            <div className={styles['table-row']} role="row" key={index}>
              <span className={styles.cell}>
                <span className={styles['cell-label']}>{roomLabel}</span>
                <span className={styles['cell-value']}>
                  {t(getRoomTypeLabelKey(room.type), { defaultValue: `${room.type}` })}
                </span>
              </span>
              <span className={styles.cell}>
                <span className={styles['cell-label']}>{dimensionsLabel}</span>
                <span className={styles['cell-value']}>{TryScrambleText(sizeLabel, shouldScramble)}</span>
              </span>
              <span className={styles.cell}>
                <span className={styles['cell-label']}>{levelLabel}</span>
                <span className={styles['cell-value']}>
                  {t(getRoomLevelLabelKey(room.level), { defaultValue: `${room.level}` })}
                </span>
              </span>
              {hasAnyFlooring && (
                <span className={styles.cell}>
                  <span className={styles['cell-label']}>{flooringLabel}</span>
                  <span className={styles['cell-value']}>{room.flooring || '–'}</span>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Rooms({ rooms, isPrivate, shouldScramble, onShowDetailsClick, variant = VARIANT_GRID }: RoomsProps) {
  const [unitOfLength, setUnitOfLength] = useState<UnitOfLength>(UNIT_OF_LENGTH_FEET);
  const otherUnitOfLength = unitOfLength === UNIT_OF_LENGTH_FEET ? UNIT_OF_LENGTH_METRES : UNIT_OF_LENGTH_FEET;
  const onToggleUnit = () => setUnitOfLength(otherUnitOfLength);

  if (variant === VARIANT_TABLE) {
    return (
      <RoomsTable
        rooms={rooms}
        isPrivate={isPrivate}
        shouldScramble={shouldScramble}
        unitOfLength={unitOfLength}
        otherUnitOfLength={otherUnitOfLength}
        onToggleUnit={onToggleUnit}
        onShowDetailsClick={onShowDetailsClick}
      />
    );
  }

  return (
    <div className={styles.component}>
      <DetailsTable isPrivate={isPrivate}>
        <section className={styles['three-columns']}>
          <header>
            <UnitsHeader
              isPrivate={isPrivate}
              otherUnitOfLength={otherUnitOfLength}
              onToggleUnit={onToggleUnit}
              onShowDetailsClick={onShowDetailsClick}
            />
          </header>
          {rooms && (
            <section>
              {rooms.map((room, index) => (
                <RoomDetails key={index} room={room} unitOfLength={unitOfLength} shouldScramble={shouldScramble} />
              ))}
            </section>
          )}
        </section>
      </DetailsTable>
    </div>
  );
}
//#endregion
